import { setAll } from "../helpers";
import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { RootState } from "../store";
import { IBaseAddressAsyncThunk } from "./interfaces";
import { addresses } from "../configs/constants";
import { ethers } from "ethers";
import { abi as DisTokenABI } from "../abis/DisToken.json";
import { abi as VYROStakeABI } from "../abis/DYNAStake.json";
import moment from "moment";
import { PLACEHOLDER_ACCOUNT } from "src/helpers/Helpers";
import { getVyroPrice } from "src/hooks/get_price_vyro";

const getEndTime = async (
  networkID: number,
  address: string,
  signer: ethers.Signer | ethers.providers.Provider | undefined
) => {
  const veVYROContract = new ethers.Contract(
    addresses[networkID].veVYRO,
    VYROStakeABI,
    signer
  );
  const lockedInfo = await veVYROContract.locked(
    address || PLACEHOLDER_ACCOUNT
  );
  const currtimestamp = moment().valueOf();
  const currtimestampS = (currtimestamp / 1000).toFixed(0);
  const endTime = Number(lockedInfo[1]) - Number(currtimestampS);
  return endTime;
};

export const loadEarnDetails = createAsyncThunk(
  "earn/loadEarn",
  async ({ networkID, provider, address }: IBaseAddressAsyncThunk) => {
    // console.log("loadEarnDetails");

    const veVYROContract = new ethers.Contract(
      addresses[networkID].veVYRO,
      VYROStakeABI,
      provider
    );
    const veVYROContractold = new ethers.Contract(
      addresses[networkID].oldveVYRO,
      VYROStakeABI,
      provider
    );
    const veVYROBalance = Number(
      ethers.utils.formatUnits(
        await veVYROContract.balanceOfAtNow(address || PLACEHOLDER_ACCOUNT),
        18
      )
    );

    const allveVYROBalance = veVYROBalance;

    const DisTokenContract = new ethers.Contract(
      addresses[networkID].dis_token,
      DisTokenABI,
      provider
    );

    const weekRewards_vyro = Number(
      ethers.utils.formatUnits(await DisTokenContract.getYieldForDuration(), 18)
    );
    const DisFeeContract = new ethers.Contract(
      addresses[networkID].dis_fee,
      DisTokenABI,
      provider
    );

    const weekRewards_usdc = Number(
      ethers.utils.formatUnits(await DisFeeContract.getYieldForDuration(), 18)
    );

    const totalSupply_veVYRO = Number(
      ethers.utils.formatUnits(await veVYROContract.totalSupplyAtNow(), 18)
    );

    const totalSupply_veVYRO_old = Number(
      ethers.utils.formatUnits(await veVYROContractold.totalSupplyAtNow(), 18)
    );

    const all_totalSupply_veVYRO = totalSupply_veVYRO + totalSupply_veVYRO_old;

    const VYROPrice = await getVyroPrice({ networkID, provider });

    let vyroApr1 = 0;
    let vyroApr2 = 0;
    let usdcApr1 = 0;
    let usdcApr2 = 0;
    // console.log("weekRewards_usdc", weekRewards_usdc, "totalSupply_veVYRO", totalSupply_veVYRO)
    if (weekRewards_vyro > 0 && all_totalSupply_veVYRO > 0) {
      vyroApr1 = ((weekRewards_vyro / 7) * 365) / all_totalSupply_veVYRO; //*(1 + 3 / 4 / 365 * 7)
      vyroApr2 = (((weekRewards_vyro / 7) * 365) / all_totalSupply_veVYRO) * 4;
    }
    if (weekRewards_usdc > 0 && all_totalSupply_veVYRO > 0) {
      usdcApr1 =
        ((weekRewards_usdc / VYROPrice / 7) * 365) / all_totalSupply_veVYRO; //* (1 + 3 / 4 / 365 * 7)
      usdcApr2 =
        (((weekRewards_usdc / VYROPrice / 7) * 365) / all_totalSupply_veVYRO) *
        4;
    }
    // console.log("vyroApr1", vyroApr1, "vyroApr2", vyroApr2, "usdcApr1", usdcApr1, "usdcApr2", usdcApr1)
    const minApr = vyroApr1 + usdcApr1;
    const maxApr = vyroApr2 + usdcApr2;

    const lockedInfo = await veVYROContract.locked(
      address || PLACEHOLDER_ACCOUNT
    );
    const VYROStakedAmount = Number(
      ethers.utils.formatUnits(lockedInfo[0], 18)
    );
    const vyroApr = (allveVYROBalance / VYROStakedAmount) * vyroApr1; // / (1 + 3 / 4 / 365 * 7)
    const usdcApr = (allveVYROBalance / VYROStakedAmount) * usdcApr1; // / (1 + 3 / 4 / 365 * 7)
    const estApr = vyroApr + usdcApr;

    const endTime = await getEndTime(
      networkID,
      address || PLACEHOLDER_ACCOUNT,
      provider
    );

    return {
      veVYROBalance: allveVYROBalance,
      vyroApr1,
      vyroApr2,
      usdcApr1,
      usdcApr2,
      minApr,
      maxApr,
      vyroApr,
      usdcApr,
      estApr,
      endTime,
    };
  }
);

const initialState = {
  loading: false,
};

const earnSlice = createSlice({
  name: "earn",
  initialState,
  reducers: {
    fetchEarnSuccess(state, action) {
      setAll(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadEarnDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadEarnDetails.fulfilled, (state, action) => {
        setAll(state, action.payload);
        state.loading = false;
      })
      .addCase(loadEarnDetails.rejected, (state, {}) => {
        state.loading = false;
      });
  },
});

const baseInfo = (state: RootState) => state.earn;
export default earnSlice.reducer;
export const { fetchEarnSuccess } = earnSlice.actions;
export const getEarnState = createSelector(baseInfo, (earn) => earn);
